import React from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface EllipsisTooltipProps {
    children: React.ReactNode;
    disableChildPointerEvents?: boolean;
    observeResize?: boolean;
    className?: string;
}

// Children may clip their own text (e.g. a cell renderer with its own ellipsis), so look for
// any clipping element in the subtree rather than comparing only the outer widths.
function isClipped(element: HTMLElement): boolean {
    return [element, ...element.querySelectorAll<HTMLElement>('*')].some(
        (node) => node.scrollWidth > node.clientWidth && getComputedStyle(node).overflowX !== 'visible',
    );
}

export function EllipsisTooltip({
    children,
    disableChildPointerEvents = false,
    observeResize = false,
    className = '',
}: EllipsisTooltipProps) {
    const cellRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);
    const [textContent, setTextContent] = React.useState('');

    React.useEffect(() => {
        const cell = cellRef.current;
        const content = contentRef.current;
        if (!cell || !content) return;

        const checkTruncation = () => {
            // innerText keeps line breaks between block children (e.g. a name over a subtitle).
            setTextContent(content.innerText || content.textContent || '');
            setIsTruncated(content.getBoundingClientRect().width > cell.getBoundingClientRect().width);
        };

        checkTruncation();
        if (!observeResize || typeof ResizeObserver === 'undefined') return;

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(cell);
        return () => resizeObserver.disconnect();
    }, [children, observeResize]);

    // Full check on interaction only: it catches self-clipping children and layout changes (e.g. column
    // resizing) without per-cell observers.
    const recheckTruncation = () => {
        if (cellRef.current) setIsTruncated(isClipped(cellRef.current));
    };

    // Silence nested hover targets (e.g. inner tooltips) but keep interactive controls clickable.
    const pointerEventsClass = disableChildPointerEvents
        ? '[&_*]:pointer-events-none [&_:is(a,button,input,select,textarea)]:pointer-events-auto'
        : '';
    const content = (
        <div
            ref={cellRef}
            onPointerEnter={recheckTruncation}
            onFocus={recheckTruncation}
            className={`w-full overflow-hidden text-ellipsis whitespace-nowrap ${pointerEventsClass} ${className}`}
        >
            <span ref={contentRef}>{children}</span>
        </div>
    );

    return (
        <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            {isTruncated && (
                <TooltipContent>
                    <p className="whitespace-pre-line">{textContent}</p>
                </TooltipContent>
            )}
        </Tooltip>
    );
}
